import {useEffect, useState} from 'react';
import {
  Button,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
  notification,
} from 'antd';
import {useHistory} from 'react-router-dom';
import {PlansService} from './PlansService';
import {LevelsService} from '../Level/LevelsService';
import {GradesService} from '../Grade/GradesService';
import {CurriculumsService} from '../Curriculums/CurriculumsService';

const plansService = new PlansService();
const levelsService = new LevelsService();
const gradesService = new GradesService();
const curriculumsService = new CurriculumsService();

export const PlanCreate = () => {
  const history = useHistory();
  const {Title, Text} = Typography;

  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [planName, setPlanName] = useState<string>('');
  const [duration, setDuration] = useState<number | null>(null);
  const [selectedLevelId, setSelectedLevelId] = useState<string>();
  const [selectedGradeId, setSelectedGradeId] = useState<string>();
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<string>();

  const [levels, setLevels] = useState<Array<any> | []>([]);
  const [grades, setGrades] = useState<Array<any> | []>([]);
  const [curriculums, setCurriculums] = useState<Array<any> | []>([]);

  // Track whether the selected curriculum is academic
  const [isAcademic, setIsAcademic] = useState<boolean>(false);

  useEffect(() => {
    console.log('🔍 [PLAN-CREATE] Fetching levels, grades, and curriculums');

    levelsService
      .getLevels()
      .then(lvls => {
        console.log('✅ [PLAN-CREATE] Levels fetched:', lvls);
        setLevels(lvls);
      })
      .catch(error => {
        setIsError(true);
        console.error('❌ [PLAN-CREATE] Error fetching levels:', error);
      });

    gradesService
      .getGrades()
      .then(grds => {
        console.log('✅ [PLAN-CREATE] Grades fetched:', grds);
        setGrades(grds);
      })
      .catch(error => {
        setIsError(true);
        console.error('❌ [PLAN-CREATE] Error fetching grades:', error);
      });

    curriculumsService
      .getCurriculums()
      .then(currs => {
        console.log('✅ [PLAN-CREATE] Curriculums fetched:', currs);
        setCurriculums(currs);
      })
      .catch(error => {
        setIsError(true);
        console.error('❌ [PLAN-CREATE] Error fetching curriculums:', error);
      });
  }, []);

  // When curriculum changes, determine if it is academic
  const handleCurriculumChange = (curriculumId: string) => {
    setSelectedCurriculumId(curriculumId);
    const selectedCurriculum = curriculums.find(
      (c: any) => (c.id || c._id) === curriculumId,
    );
    const academic = selectedCurriculum?.isAcademic === true;
    setIsAcademic(academic);

    // Clear the opposite selection
    if (academic) {
      setSelectedLevelId(undefined);
    } else {
      setSelectedGradeId(undefined);
    }
  };

  const handleCreate = async () => {
    console.log('🔍 [PLAN-CREATE] Creating plan with data:', {
      planName,
      duration,
      selectedLevelId,
      selectedGradeId,
      selectedCurriculumId,
    });

    if (!planName || !duration || !selectedCurriculumId) {
      notification.warning({
        message: 'Missing Fields',
        description: 'Please fill in Plan Name, Duration, and Curriculum.',
      });
      return;
    }

    // Validate: academic requires grade, non-academic requires level
    if (isAcademic && !selectedGradeId) {
      notification.warning({
        message: 'Missing Grade',
        description: 'This is an academic curriculum. Please select a Grade.',
      });
      return;
    }

    if (!isAcademic && !selectedLevelId) {
      notification.warning({
        message: 'Missing Level',
        description:
          'This is a non-academic curriculum. Please select a Level.',
      });
      return;
    }

    setIsLoading(true);

    await plansService
      .newCreatePlan(
        planName,
        duration,
        isAcademic ? undefined : selectedLevelId,
        isAcademic ? selectedGradeId : undefined,
        selectedCurriculumId,
      )
      .then((response: any) => {
        console.log('✅ [PLAN-CREATE] Plan created successfully:', response);
        notification.success({
          message: 'Plan Created!',
          description: `"${planName}" has been created successfully.`,
          duration: 4,
        });
        history.push('/plans');
      })
      .catch(err => {
        console.error('❌ [PLAN-CREATE] Error creating plan:', err);
        notification.error({
          message: 'Creation Failed',
          description:
            err?.message || 'Failed to create plan. Please try again.',
          duration: 6,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Create Plan</Title>
      <>
        <Row>
          <Col span={12}>
            <div style={{margin: 20}}>
              <Text style={{display: 'block'}} strong={true}>
                Plan Name *
              </Text>
              <Input
                placeholder={'Enter Plan Name'}
                onChange={e => setPlanName(e.target.value)}
                value={planName}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <div style={{margin: 20}}>
              <Text style={{display: 'block'}} strong={true}>
                Duration (Days) *
              </Text>
              <InputNumber
                placeholder={'Enter duration in days'}
                onChange={value => setDuration(value)}
                value={duration}
                style={{width: '100%'}}
                min={1}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <div style={{margin: 20}}>
              <Text style={{display: 'block'}} strong={true}>
                Select Curriculum *
              </Text>
              <Select
                value={selectedCurriculumId}
                showSearch
                style={{width: '100%'}}
                placeholder="Select a Curriculum"
                optionFilterProp="children"
                onChange={handleCurriculumChange}>
                {curriculums?.map(curriculum => (
                  <Select.Option
                    key={curriculum.id || curriculum._id}
                    value={curriculum.id || curriculum._id}>
                    {curriculum.name}
                    {curriculum.isAcademic ? ' (Academic)' : ''}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>

        {/* Show Level selector for non-academic curriculums */}
        {!isAcademic && (
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Select Level *
                </Text>
                <Select
                  value={selectedLevelId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Level"
                  optionFilterProp="children"
                  onChange={levelId => setSelectedLevelId(levelId)}>
                  {levels?.map(level => (
                    <Select.Option
                      key={level.id || level._id}
                      value={level.id || level._id}>
                      {level.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>
        )}

        {/* Show Grade selector for academic curriculums */}
        {isAcademic && (
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Select Grade *
                </Text>
                <Select
                  value={selectedGradeId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Grade"
                  optionFilterProp="children"
                  onChange={gradeId => setSelectedGradeId(gradeId)}>
                  {grades?.map(grade => (
                    <Select.Option
                      key={grade.id || grade._id}
                      value={grade.id || grade._id}>
                      {grade.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>
        )}
      </>

      <Button
        type="primary"
        size="large"
        loading={isLoading}
        onClick={handleCreate}
        style={{margin: 20}}>
        Create Plan
      </Button>
    </div>
  );
};
